// ./src/utils/api/ExpenseSummaryReportService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { CurrencyAmount, ConsumptionData } from "@/types";

// ============================================
// GraphQL Query
// ============================================

const EXPENSE_SUMMARY_REPORT_QUERY = gql`
  query ReportExpenseSummary($filter: ExpenseSummaryReportFilter) {
    reportExpenseSummary(filter: $filter) {
      code
      data {
        # Period info
        dateFrom
        dateTo
        periodDays

        # Totals (Home Currency)
        totalCostHc
        refuelsCostHc
        expensesCostHc

        # Daily averages (Home Currency)
        avgTotalCostPerDayHc
        avgRefuelsCostPerDayHc
        avgExpensesCostPerDayHc

        # Foreign currency totals
        foreignCurrencyTotals {
          currency
          amount
          recordsCount
        }
        totalForeignRecordsCount

        # Refuels detail
        refuels {
          totalCostHc
          recordsCountHc
          totalVolume
          avgPricePerVolumeHc
          foreignCurrencies {
            currency
            amount
            recordsCount
          }
          totalForeignRecordsCount
        }

        # Expenses detail (non-refuel)
        expenses {
          totalCostHc
          recordsCountHc
          foreignCurrencies {
            currency
            amount
            recordsCount
          }
          totalForeignRecordsCount
        }

        # Fuel metrics
        fuelPurchased
        refuelsCount

        # Mileage metrics
        startOdometer
        endOdometer
        mileage
        avgMileagePerDay

        # Consumption metrics (NEW - with fuel type breakdown)
        consumption {
          byFuelType {
            fuelType
            consumption
            consumptionUnit
            fuelUsed
            fuelUnit
            distance
            distanceUnit
            confidence
            confidenceReasons
            vehiclesCount
            refuelsCount
            dataPointsCount
          }
          totalDistance
          distanceUnit
          totalVehiclesCount
        }

        # Deprecated consumption fields (for backward compatibility)
        consumptionValue
        consumptionConfidence

        # Efficiency metrics
        costPerDistanceHc

        # Record counts
        totalRecordsCount
        vehiclesCount

        # Breakdowns
        expensesByCategory {
          categoryId
          categoryCode
          categoryName
          totalAmountHc
          recordsCountHc
          percentageHc
          foreignCurrencies {
            currency
            amount
            recordsCount
          }
          totalForeignRecordsCount
        }

        expensesByKind {
          kindId
          kindCode
          kindName
          categoryId
          categoryCode
          totalAmountHc
          recordsCountHc
          percentageHc
          foreignCurrencies {
            currency
            amount
            recordsCount
          }
          totalForeignRecordsCount
        }

        # User preferences (for frontend display)
        distanceUnit
        volumeUnit
        consumptionUnit
        homeCurrency
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// ============================================
// Types
// ============================================

/**
 * Filter input for expense summary report
 */
export interface ExpenseSummaryReportFilter {
  /** Filter by car IDs (optional - all cars if not specified) */
  carId?: string[] | null;
  /** Filter by tag IDs */
  tagId?: string[] | null;
  /** Start date (YYYY-MM-DD format, UTC) */
  dateFrom?: string | null;
  /** End date (YYYY-MM-DD format, UTC) */
  dateTo?: string | null;
}

/**
 * Summary of expenses by category
 */
export interface ExpenseSummaryByCategory {
  /** Category ID */
  categoryId?: number;
  /** Category code */
  categoryCode?: string;
  /** Localized category name */
  categoryName?: string;
  /** Total amount in home currency */
  totalAmountHc?: number;
  /** Number of records in home currency */
  recordsCountHc?: number;
  /** Percentage of total expenses */
  percentageHc?: number;
  /** Foreign currency breakdowns */
  foreignCurrencies?: CurrencyAmount[];
  /** Total records in foreign currencies */
  totalForeignRecordsCount?: number;
}

/**
 * Summary of expenses by kind (sub-category)
 */
export interface ExpenseSummaryByKind {
  /** Kind ID */
  kindId?: number;
  /** Kind code */
  kindCode?: string;
  /** Localized kind name */
  kindName?: string;
  /** Parent category ID */
  categoryId?: number;
  /** Parent category code */
  categoryCode?: string;
  /** Total amount in home currency */
  totalAmountHc?: number;
  /** Number of records in home currency */
  recordsCountHc?: number;
  /** Percentage of total expenses */
  percentageHc?: number;
  /** Foreign currency breakdowns */
  foreignCurrencies?: CurrencyAmount[];
  /** Total records in foreign currencies */
  totalForeignRecordsCount?: number;
}

/**
 * Refuels summary section
 */
export interface RefuelsSummary {
  /** Total cost in home currency */
  totalCostHc?: number;
  /** Number of refuel records in home currency */
  recordsCountHc?: number;
  /** Total fuel volume purchased (in user's volume unit) */
  totalVolume?: number;
  /** Average price per volume unit in home currency */
  avgPricePerVolumeHc?: number;
  /** Foreign currency breakdowns */
  foreignCurrencies?: CurrencyAmount[];
  /** Total records in foreign currencies */
  totalForeignRecordsCount?: number;
}

/**
 * Non-refuel expenses summary section
 */
export interface ExpensesSummary {
  /** Total cost in home currency */
  totalCostHc?: number;
  /** Number of expense records in home currency */
  recordsCountHc?: number;
  /** Foreign currency breakdowns */
  foreignCurrencies?: CurrencyAmount[];
  /** Total records in foreign currencies */
  totalForeignRecordsCount?: number;
}

/**
 * Complete expense summary report data
 */
export interface ExpenseSummaryReport {
  // Period info
  /** Report start date */
  dateFrom?: string;
  /** Report end date */
  dateTo?: string;
  /** Number of days in the period */
  periodDays?: number;

  // Totals (Home Currency)
  /** Total cost (refuels + expenses) in home currency */
  totalCostHc?: number;
  /** Total refuels cost in home currency */
  refuelsCostHc?: number;
  /** Total expenses cost in home currency */
  expensesCostHc?: number;

  // Daily averages (Home Currency)
  /** Average total cost per day */
  avgTotalCostPerDayHc?: number;
  /** Average refuels cost per day */
  avgRefuelsCostPerDayHc?: number;
  /** Average expenses cost per day */
  avgExpensesCostPerDayHc?: number;

  // Foreign currency totals
  /** Breakdown of totals by foreign currency */
  foreignCurrencyTotals?: CurrencyAmount[];
  /** Total records in foreign currencies */
  totalForeignRecordsCount?: number;

  // Detailed sections
  /** Refuels summary */
  refuels?: RefuelsSummary;
  /** Expenses summary (non-refuel) */
  expenses?: ExpensesSummary;

  // Fuel metrics
  /** Total fuel volume purchased */
  fuelPurchased?: number;
  /** Number of refuel events */
  refuelsCount?: number;

  // Mileage metrics
  /** Starting odometer reading */
  startOdometer?: number;
  /** Ending odometer reading */
  endOdometer?: number;
  /** Total mileage in the period */
  mileage?: number;
  /** Average mileage per day */
  avgMileagePerDay?: number;

  // Consumption metrics (NEW - with fuel type breakdown)
  /**
   * Detailed consumption data grouped by fuel type.
   * Includes confidence levels and supporting statistics.
   */
  consumption?: ConsumptionData;

  /**
   * @deprecated Use consumption.byFuelType[0].consumption instead.
   * Legacy field for backward compatibility - returns first fuel type's consumption.
   */
  consumptionValue?: number;

  /**
   * @deprecated Use consumption.byFuelType[0].confidence instead.
   * Legacy field for backward compatibility - returns first fuel type's confidence.
   */
  consumptionConfidence?: string;

  // Efficiency metrics
  /** Cost per distance unit in home currency */
  costPerDistanceHc?: number;

  // Record counts
  /** Total number of records */
  totalRecordsCount?: number;
  /** Number of vehicles in the report */
  vehiclesCount?: number;

  // Breakdowns
  /** Expenses grouped by category */
  expensesByCategory?: ExpenseSummaryByCategory[];
  /** Expenses grouped by kind */
  expensesByKind?: ExpenseSummaryByKind[];

  // User preferences (for display formatting)
  /** User's distance unit (km/mi) */
  distanceUnit?: string;
  /** User's volume unit (L/gal) */
  volumeUnit?: string;
  /** User's consumption unit (L/100km, mpg, etc.) */
  consumptionUnit?: string;
  /** User's home currency code */
  homeCurrency?: string;
}

// ============================================
// Service Class
// ============================================

/**
 * ExpenseSummaryReportService handles API calls for the expense summary report.
 *
 * This report provides a comprehensive overview of vehicle expenses including:
 * - Total costs broken down by refuels and other expenses
 * - Daily averages for cost tracking
 * - Mileage and fuel efficiency metrics
 * - Category and kind-level expense breakdowns
 * - Foreign currency tracking
 * - Multi-fuel-type consumption data with confidence levels
 */
export class ExpenseSummaryReportService {
  /**
   * Get expense summary report with optional filters.
   *
   * @param filter - Optional filters (carId, tagId, dateFrom, dateTo)
   * @returns OpResult containing ExpenseSummaryReport data
   *
   * @example
   * // Get report for all cars, last 30 days
   * const result = await ExpenseSummaryReportService.get({
   *   dateFrom: '2025-12-13',
   *   dateTo: '2026-01-12'
   * });
   *
   * @example
   * // Get report for specific car with tags
   * const result = await ExpenseSummaryReportService.get({
   *   carId: ['car-uuid-123'],
   *   tagId: ['tag-1', 'tag-2'],
   *   dateFrom: '2025-01-01',
   *   dateTo: '2025-12-31'
   * });
   *
   * @example
   * // Access consumption data by fuel type
   * const result = await ExpenseSummaryReportService.get();
   * const report = result.data?.[0];
   * report?.consumption?.byFuelType?.forEach(ft => {
   *   console.log(`${ft.fuelType}: ${ft.consumption} ${ft.consumptionUnit}`);
   * });
   */
  static async get(filter?: ExpenseSummaryReportFilter): Promise<OpResult> {
    const result = await ApiService.executeQuery(EXPENSE_SUMMARY_REPORT_QUERY, {
      filter: filter || {},
    });
    return result;
  }
}